import React from 'react';
import { BookOpen, Flag, Check, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// Neutral order list: no hints about which orders are safe until the review is submitted
export const OrderReviewList = ({ orders, flagged, appropriate, onAppropriate, onFlag, onLookup, reasonLabel, flaggedReasons }) => (
  <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="order-review-list">
    {orders.map((o) => {
      const isFlagged = !!flagged[o.flagKey];
      const isOk = !!appropriate[o.flagKey];
      return (
        <div
          key={o.flagKey}
          className={`p-3 rounded-lg border-2 transition-colors ${isFlagged ? 'border-red-400 bg-red-50' : isOk ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'}`}
          data-testid={`order-row-${o.flagKey}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900">{o.name}</span>
            {o.medInfo && (
              <button onClick={() => onLookup(o)} className="p-1 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700" title="View drug information" data-testid={`order-lookup-${o.flagKey}`}>
                <BookOpen className="w-3 h-3" />
              </button>
            )}
          </div>
          <p className="text-xs text-slate-600 mt-1">
            {o.route && <><span className="font-medium">Route:</span> {o.route} | </>}
            {o.frequency && <><span className="font-medium">Frequency:</span> {o.frequency}</>}
            {o.indication && <> | <span className="font-medium">Indication:</span> {o.indication}</>}
          </p>
          {isFlagged && flaggedReasons[o.flagKey]?.reasons?.length > 0 && (
            <p className="mt-2 p-2 bg-slate-100 rounded text-xs text-slate-700">
              <span className="font-bold">Your reasoning: </span>
              {flaggedReasons[o.flagKey].reasons.map(reasonLabel).join(', ')}
              {flaggedReasons[o.flagKey].notes && <span className="italic"> - "{flaggedReasons[o.flagKey].notes}"</span>}
            </p>
          )}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => onAppropriate(o)}
              className={`flex-1 text-xs py-1.5 rounded border flex items-center justify-center gap-1 transition-colors ${isOk ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-300 text-slate-800 hover:border-emerald-500'}`}
              data-testid={`order-appropriate-${o.flagKey}`}
            >
              <Check className="w-3.5 h-3.5" /> Appropriate
            </button>
            <button
              onClick={() => onFlag(o)}
              className={`flex-1 text-xs py-1.5 rounded border flex items-center justify-center gap-1 transition-colors ${isFlagged ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-slate-300 text-slate-800 hover:border-red-500'}`}
              data-testid={`order-flag-${o.flagKey}`}
            >
              <Flag className="w-3.5 h-3.5" /> {isFlagged ? 'Flagged' : 'Flag concern'}
            </button>
          </div>
        </div>
      );
    })}
  </div>
);

const DECISION_LABEL = { flag: 'You flagged', appropriate: 'You marked appropriate', none: 'Not reviewed' };

export const ReviewResults = ({ results }) => (
  <div className="space-y-2 mb-3" data-testid="chart-review-results">
    <h4 className="text-sm font-bold text-slate-800">Order-by-order results:</h4>
    {results.map((r, i) => {
      const Icon = r.correct ? CheckCircle : r.decision === 'none' ? AlertCircle : XCircle;
      return (
        <div key={i} className={`p-2 rounded border text-xs ${r.correct ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid={`review-result-${i}`}>
          <div className="flex items-center gap-1 font-semibold text-slate-900">
            <Icon className={`w-3.5 h-3.5 ${r.correct ? 'text-emerald-700' : 'text-red-700'}`} />
            {r.item}
            <span className={`ml-auto ${r.points >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{r.points > 0 ? '+' : ''}{r.points}</span>
          </div>
          <p className="text-slate-600 mt-0.5">{DECISION_LABEL[r.decision] || ''}</p>
          <p className="text-slate-800 mt-0.5">{r.rationale}</p>
        </div>
      );
    })}
  </div>
);
